import { checkIsInternalCompany } from 'app/auth/authUtils';
import _ from 'lodash';
import memoizeOne from 'memoize-one';
import { createSelector } from 'reselect';

import {
  ChecklistEntityType,
  ChecklistItemDeterminationType,
  ChecklistTargetType,
} from 'constants/checklists';

import { selectMasterCompanyId } from 'store/auth/selectors';

import { getSystem } from './system.selectors';

const selectChecklists = createSelector(getSystem, ({ checklists }) => checklists);

export const selectActiveChecklistTargetType = createSelector(
  getSystem,
  ({ checklistsTargetType }) => checklistsTargetType || ChecklistTargetType.LOAN,
);

export const selectChecklistById = memoizeOne(id =>
  createSelector(selectMasterCompanyId, selectChecklists, (masterCompanyId, checklists) => {
    const isInternalCompany = checkIsInternalCompany({ companyId: masterCompanyId });
    const checklist = checklists[id];

    return {
      ...checklist,
      isEditAllowedForCurrentCompany: checklist?.isGlobal ? isInternalCompany : true,
      visibilityExpression:
        checklist?.visibilityExpressionOverride?.value ||
        checklist?.visibilityExpression?.value ||
        '',
      visibilityUserFriendlyExpression:
        checklist?.visibilityExpressionOverride?.userFriendlyValue ||
        checklist?.visibilityExpression?.userFriendlyValue ||
        '',
      originalVisibilityUserFriendlyExpression:
        checklist?.visibilityExpression?.userFriendlyValue || '',
      overrideVisibilityExpression: checklist?.visibilityExpressionOverride?.value || '',
      overrideVisibilityUserFriendlyExpression:
        checklist?.visibilityExpressionOverride?.userFriendlyValue || '',
      hasVisibilityOverride: !!checklist?.visibilityExpressionOverride,
    };
  }),
);

const selectChecklistsEnhancedSchema = createSelector(
  getSystem,
  selectActiveChecklistTargetType,
  ({ checklistsSchema }, targetType) => checklistsSchema?.[targetType] || {},
);

export const selectChecklistsSchema = createSelector(
  selectChecklistsEnhancedSchema,
  ({ visibilitySchema }) => visibilitySchema,
);

export const selectChecklistsResultSchema = createSelector(
  selectChecklistsEnhancedSchema,
  ({ resultSchema }) => resultSchema,
);

const selectChecklistsSchemaCompanyId = createSelector(
  getSystem,
  ({ checklistsSchema }) => checklistsSchema?.companyId,
);

// Presence is tested against undefined because a loaded schema can legitimately be an empty object, which
// truthiness would re-fetch forever. The company must match because impersonation swaps companies without
// resetting the store.
export const selectAreChecklistsSchemasLoaded = createSelector(
  selectChecklistsEnhancedSchema,
  selectChecklistsSchemaCompanyId,
  selectMasterCompanyId,
  ({ visibilitySchema, resultSchema }, schemaCompanyId, masterCompanyId) =>
    schemaCompanyId === masterCompanyId &&
    visibilitySchema !== undefined &&
    resultSchema !== undefined,
);

const selectChecklistsValues = createSelector(selectChecklists, Object.values);

export const selectChecklistsOptions = createSelector(selectChecklistsValues, values =>
  values.map(({ name, id }) => ({ name, value: id })),
);

// Options for the "Completeness Checklist" picker — the HIDDEN completeness checklists (the global
// master + any company-specific ones), which the visible-only `selectChecklistsOptions` omits.
// Sourced from the hidden-inclusive search (DEV-4603). Disabled checklists never generate results,
// so they are not offered.
const selectCompletenessChecklistOptionsList = createSelector(
  getSystem,
  ({ completenessChecklistOptions }) => completenessChecklistOptions,
);

export const selectCompletenessChecklistOptions = createSelector(
  selectCompletenessChecklistOptionsList,
  checklists =>
    checklists
      .filter(({ isHidden, isEnabled }) => isHidden && isEnabled)
      .map(({ name, id }) => ({ name, value: id })),
);

const selectChecklistsSortedValues = createSelector(selectChecklistsValues, checklists =>
  _.sortBy(checklists, [checklist => _.toLower(checklist.name)]),
);

export const selectChecklistsFlattenList = createSelector(
  selectMasterCompanyId,
  selectChecklistsSortedValues,
  (masterCompanyId, checklists) =>
    checklists.reduce((acc, { categories, ...checklist }) => {
      const isInternalCompany = checkIsInternalCompany({ companyId: masterCompanyId });
      const rootCategory = categories?.find(({ isRoot }) => isRoot);
      const rootCategoryId = rootCategory?.id;

      categories?.forEach(({ items, ...category }) => {
        const isRootCategory = category.isRoot;
        const isCategoryGlobal = category.isGlobal ?? checklist?.isGlobal;
        const isCategoryEditable = isCategoryGlobal ? isInternalCompany : true;

        if (!isRootCategory) {
          acc.push({
            ...category,
            isGlobal: isCategoryGlobal,
            isDisabledForCompany: category.isDisabledForCompany,
            isEditAllowedForCurrentCompany: isCategoryEditable,
            parentId: category.checklistId,
            type: ChecklistEntityType.CATEGORY,
            visibilityExpression:
              category.visibilityExpressionOverride?.value ||
              category.visibilityExpression?.value ||
              '',
            visibilityUserFriendlyExpression:
              category.visibilityExpressionOverride?.userFriendlyValue ||
              category.visibilityExpression?.userFriendlyValue ||
              '',
            originalVisibilityUserFriendlyExpression:
              category.visibilityExpression?.userFriendlyValue || '',
            overrideVisibilityExpression: category.visibilityExpressionOverride?.value || '',
            overrideVisibilityUserFriendlyExpression:
              category.visibilityExpressionOverride?.userFriendlyValue || '',
            hasVisibilityOverride: !!category.visibilityExpressionOverride,
          });
        }

        const sortedItems = _.flow(
          items =>
            _.map(items, item => {
              const isItemGlobal = item.isGlobal ?? checklist?.isGlobal;

              return {
                ...item,
                isGlobal: isItemGlobal,
                isDisabledForCompany: item.isDisabledForCompany,
                isEditAllowedForCurrentCompany: isItemGlobal ? isInternalCompany : true,
                expression: item.expression?.value || '',
                userFriendlyExpression: item.expression?.userFriendlyValue || '',
                visibilityExpression:
                  item.visibilityExpressionOverride?.value ||
                  item.visibilityExpression?.value ||
                  '',
                visibilityUserFriendlyExpression:
                  item.visibilityExpressionOverride?.userFriendlyValue ||
                  item.visibilityExpression?.userFriendlyValue ||
                  '',
                originalVisibilityUserFriendlyExpression:
                  item.visibilityExpression?.userFriendlyValue || '',
                overrideVisibilityExpression: item.visibilityExpressionOverride?.value || '',
                overrideVisibilityUserFriendlyExpression:
                  item.visibilityExpressionOverride?.userFriendlyValue || '',
                hasVisibilityOverride: !!item.visibilityExpressionOverride,
                parentId: item.parentId || (isRootCategory ? item.checklistId : item.categoryId),
                initialParentId: item.parentId,
                type: ChecklistEntityType.ITEM,
              };
            }),
          items => _.groupBy(items, 'parentId'),
          items => _.flatMap(_.values(items), group => _.sortBy(group, 'orderSequence')),
        )(items);

        acc.push(...sortedItems);
      });

      acc.push({
        ...checklist,
        isGlobal: checklist?.isGlobal,
        isDisabledForCompany: checklist?.isDisabledForCompany,
        isEditAllowedForCurrentCompany: checklist?.isGlobal ? isInternalCompany : true,
        checklistId: checklist.id,
        parentId: null,
        type: ChecklistEntityType.CHECKLIST,
        rootCategoryId,
        visibilityExpression:
          checklist?.visibilityExpressionOverride?.value ||
          checklist?.visibilityExpression?.value ||
          '',
        visibilityUserFriendlyExpression:
          checklist?.visibilityExpressionOverride?.userFriendlyValue ||
          checklist?.visibilityExpression?.userFriendlyValue ||
          '',
        originalVisibilityUserFriendlyExpression:
          checklist?.visibilityExpression?.userFriendlyValue || '',
        overrideVisibilityExpression: checklist?.visibilityExpressionOverride?.value || '',
        overrideVisibilityUserFriendlyExpression:
          checklist?.visibilityExpressionOverride?.userFriendlyValue || '',
        hasVisibilityOverride: !!checklist?.visibilityExpressionOverride,
      });

      return acc;
    }, []),
);

export const selectFlattenChecklistsValues = createSelector(
  selectChecklistsFlattenList,
  checklists => _.keyBy(checklists, 'id'),
);

const selectChecklistsEntitiesGrouppedByParentIds = createSelector(
  selectChecklistsFlattenList,
  checklists => _.groupBy(checklists, 'parentId'),
);

export const selectChecklistEntityById = memoizeOne(id =>
  createSelector(selectFlattenChecklistsValues, checklists => checklists[id]),
);

export const selectChecklistsHydration = createSelector(
  getSystem,
  ({ checklistsHydration }) => checklistsHydration,
);

const selectParentChecklistEntitiesIds = memoizeOne(id =>
  createSelector(selectFlattenChecklistsValues, checklists => {
    const collectParentIds = (currentId, acc) => {
      if (!currentId) {
        return acc;
      }

      return collectParentIds(checklists[currentId]?.parentId, [...acc, currentId]);
    };

    return collectParentIds(checklists[id]?.parentId, []);
  }),
);

export const selectIsChecklistEntityParent = memoizeOne(entityId =>
  createSelector(selectChecklistsEntitiesGrouppedByParentIds, grouppedEntities => {
    return !!grouppedEntities[entityId]?.length;
  }),
);

export const selectExtendedChecklistItemById = memoizeOne(id =>
  createSelector(
    [selectFlattenChecklistsValues, selectParentChecklistEntitiesIds(id)],
    (checklistsEntities, parentIds) => {
      const checklistItem = checklistsEntities[id] || {};
      const checklistParentItem = checklistsEntities[checklistItem.parentId];
      const inheritedVisibilityExpressions = parentIds
        .map(id => checklistsEntities[id].visibilityUserFriendlyExpression)
        .filter(Boolean);

      return {
        ...checklistItem,
        inheritedExpressionResult:
          checklistParentItem?.expressionResult !== ChecklistItemDeterminationType.VALUE
            ? checklistParentItem?.expressionResult
            : '',
        inheritedVisibilityExpressions: inheritedVisibilityExpressions.length
          ? `AND(${inheritedVisibilityExpressions.join(', ')})`
          : '',
      };
    },
  ),
);

// Whether the loaded tree has a seat for this node. The node-read merge is a replace-in-place, so
// without a seat it silently no-ops — and a node marked hydrated off a read that never landed is the
// blank-save the per-node gate exists to stop. Lives here rather than in the action because it walks
// the raw tree, and the shape is this module's to know.
export const selectHasChecklistNodeSeat = (state, { checklistId, categoryId, itemId }) => {
  const categories = selectChecklists(state)[checklistId]?.categories;
  const category = categories?.find(({ id }) => id === categoryId);

  return !!category && (!itemId || !!category.items?.some(({ id }) => id === itemId));
};
